import { formatHumanName } from "@medplum/core";
import type { HumanName, Patient } from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import { IconLogout, IconSearch, IconUserCircle } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import {
  Box,
  Button,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { getPractitionerRoleDisplayLabel } from "@benchmark/core/constants";
import {
  buildPatientSearchGraphQLQuery,
  getPatientsFromResponse,
} from "./connectionsAppShell.search";
import type { PatientSearchGraphQLResponse } from "./connectionsAppShell.search";
import { performLogout } from "@/app/auth/performAuthReset";
import { useAuthSession } from "@/app/auth/AuthSessionContext";
import { useCurrentUserOptional } from "@/app/auth/CurrentUserContext";
import { ClinicalNotificationBell } from "@/components/domain/clinical-notification/ClinicalNotificationBell";
import { DemoModeToggle } from "./DemoModeToggle";

import gradientBackdropStyles from "@/styles/ConnectionsGradientBackdrop.module.css";
import classes from "./ConnectionsAppShell.module.css";
import { SpotlightKeyboardShortcuts } from "./SpotlightKeyboardShortcuts";

/**
 * Sidebar collapsed-state localStorage key.
 * @agentic connections
 */
const SIDEBAR_COLLAPSED_KEY = "connections.sidebarCollapsed";

/**
 * Ignored query params for active-link matching.
 * @agentic connections
 */
const IGNORED_QUERY_PARAMS = ["_count", "_offset"];

/**
 * Sidebar link configuration for the custom shell.
 * @agentic connections
 */
export type ConnectionsNavLink = {
  readonly icon: ReactNode;
  readonly label: string;
  readonly href: string;
  readonly disabled?: boolean;
  readonly tooltip?: string;
};

/**
 * Sidebar grouped section configuration for the custom shell.
 * @agentic connections
 */
export type ConnectionsNavMenu = {
  readonly title: string;
  readonly links: readonly ConnectionsNavLink[];
};

/**
 * Props for the custom app-owned shell wrapper.
 * @agentic connections
 */
export type ConnectionsAppShellProps = {
  readonly logo: ReactNode;
  readonly menus: readonly ConnectionsNavMenu[];
  readonly children: ReactNode;
  readonly version?: string;
  /**
   * Presenter demo-mode control, rendered beside the version label. Omitted
   * (undefined) when the current user may not use demo mode, in which case no
   * toggle renders at all. See `useDemoMode`.
   */
  readonly demoMode?: {
    readonly enabled: boolean;
    readonly onToggle: () => void;
  };
};

/**
 * Search state machine values for spotlight UX.
 * @agentic connections
 */
type SpotlightStatus = "idle" | "loading" | "results" | "empty" | "error";

/**
 * Custom connections shell with V2-style left sidebar and spotlight search.
 * @agentic connections
 */
export const ConnectionsAppShell = ({
  logo,
  menus,
  children,
  version,
  demoMode,
}: ConnectionsAppShellProps) => {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const { resetSession } = useAuthSession();
  const currentUser = useCurrentUserOptional();

  // Compact role label shown under the user's name in the profile menu. Reads
  // the shared current-user context optionally so the shell still renders (name
  // only) when mounted outside `<CurrentUserProvider>` (tests/storybook), while
  // loading, or for a role-less user. The label is derived from the primary
  // PractitionerRole's own code (not just the curated business codes) so
  // standard HL7 roles such as "ICT professional" still display.
  const roleLabel = useMemo(() => {
    if (!currentUser || currentUser.isLoading) return undefined;
    const primaryLabel = getPractitionerRoleDisplayLabel(
      currentUser.primaryPractitionerRole,
    );
    if (!primaryLabel) return undefined;
    const extraRoles = currentUser.practitionerRoles.length - 1;
    return extraRoles > 0 ? `${primaryLabel} +${extraRoles}` : primaryLabel;
  }, [currentUser]);

  // Full, comma-separated role list surfaced on hover when the user holds
  // multiple roles (the visible label only shows the primary + a "+N" hint).
  const roleTitle = useMemo(() => {
    if (!currentUser || currentUser.practitionerRoles.length <= 1) {
      return undefined;
    }
    return currentUser.practitionerRoles
      .map((role) => getPractitionerRoleDisplayLabel(role))
      .filter((label): label is string => Boolean(label))
      .join(", ");
  }, [currentUser]);

  const [isCollapsed, setIsCollapsed] = useState(getInitialCollapsedState());
  const [showLogoutAction, setShowLogoutAction] = useState(false);
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<SpotlightStatus>("idle");
  const [results, setResults] = useState<Patient[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const showResultsPanel =
    status === "loading" ||
    status === "results" ||
    status === "empty" ||
    status === "error";

  const activeLink = useMemo(() => {
    const currentSearchParams = new URLSearchParams(location.search);
    return getActiveLink(location.pathname, currentSearchParams, menus);
  }, [location.pathname, location.search, menus]);

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    const onHotkey = (event: globalThis.KeyboardEvent) => {
      const isOpenShortcut =
        (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k";
      if (!isOpenShortcut) {
        return;
      }
      event.preventDefault();
      setSpotlightOpen(true);
    };

    window.addEventListener("keydown", onHotkey);
    return () => window.removeEventListener("keydown", onHotkey);
  }, []);

  useEffect(() => {
    if (!spotlightOpen) {
      return;
    }

    const trimmed = query.trim();
    if (!trimmed) {
      return;
    }

    const controller = new AbortController();

    const timer = window.setTimeout(() => {
      const graphqlQuery = buildPatientSearchGraphQLQuery(trimmed);

      medplum
        .graphql(graphqlQuery, undefined, undefined, {
          signal: controller.signal,
        })
        .then((response) => {
          if (controller.signal.aborted) {
            return;
          }

          const patients = getPatientsFromResponse(
            response as PatientSearchGraphQLResponse,
            trimmed,
          );
          setResults(patients);
          setHighlightedIndex(patients.length > 0 ? 0 : -1);
          setStatus(patients.length > 0 ? "results" : "empty");
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setResults([]);
            setHighlightedIndex(-1);
            setStatus("error");
          }
        });
    }, 200);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [medplum, query, spotlightOpen]);

  /**
   * Handles keyboard navigation and selection in spotlight results.
   * @agentic connections
   */
  const onSearchInputKeyDown = (
    event: ReactKeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) => Math.min(results.length - 1, prev + 1));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prev) => Math.max(0, prev - 1));
      return;
    }

    if (event.key === "Enter") {
      if (highlightedIndex >= 0 && highlightedIndex < results.length) {
        event.preventDefault();
        selectPatient(results[highlightedIndex]);
      }
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closeSpotlight();
    }
  };

  /**
   * Navigates to selected patient and closes spotlight.
   * @agentic connections
   */
  const selectPatient = (patient: Patient) => {
    if (!patient.id) {
      return;
    }
    closeSpotlight();
    navigate(`/Patient/${patient.id}`);
  };

  /**
   * Resets and closes spotlight search UI.
   * @agentic connections
   */
  const closeSpotlight = () => {
    setSpotlightOpen(false);
    setQuery("");
    setResults([]);
    setStatus("idle");
    setHighlightedIndex(-1);
  };

  /**
   * Updates spotlight query and local loading/idle state.
   * @agentic connections
   */
  const onQueryChange = (nextQuery: string) => {
    setQuery(nextQuery);

    if (!nextQuery.trim()) {
      setResults([]);
      setStatus("idle");
      setHighlightedIndex(-1);
      return;
    }

    setStatus("loading");
    setHighlightedIndex(-1);
  };

  /**
   * Performs sign-out and routes to the sign-in page via the soft
   * auth-reset flow. `performLogout` revokes the Medplum session (clearing
   * `profile`) and then `performAuthReset` clears the `wasAuthenticated`
   * latch — both are required or the auth gate keeps the user signed in.
   * No toast for the manual reason (sign-out is intentional).
   * @agentic connections
   */
  const signOut = () =>
    performLogout("manual", {
      signOut: () => medplum.signOut(),
      clearSession: () => medplum.clear(),
      navigate: (to) => {
        const result = navigate(to);
        if (result && typeof (result as Promise<void>).catch === "function") {
          (result as Promise<void>).catch(console.error);
        }
      },
      location,
      resetSession,
      notify: (n) => notifications.show(n),
    });

  /**
   * Handles conditional nav action for enabled links.
   * @agentic connections
   */
  const onNavClick = (href: string, disabled?: boolean) => {
    if (disabled) {
      return;
    }
    navigate(href);
  };

  return (
    <div
      className={`${gradientBackdropStyles.gradientBackdrop} ${classes.shell}`}
    >
      <aside
        className={classes.sidebar}
        data-collapsed={isCollapsed || undefined}
      >
        <Stack className={classes.sidebarContent} justify="space-between">
          <Stack gap="xs">
            <UnstyledButton
              className={classes.logoButton}
              onClick={() => setIsCollapsed((prev) => !prev)}
              aria-label="Toggle sidebar"
            >
              {isCollapsed ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 256 256"
                  className={classes.collapsedLogo}
                  aria-label="Benchmark Health"
                >
                  <path
                    fill="white"
                    d="M74.93,185.92H29.11v-115.79h46.15c22.33,0,34.74,10.59,34.74,28.12,0,12.24-7.28,23.98-22.16,25.97,16.21,1.65,25.97,13.07,25.97,27.46,0,21.01-14.06,34.24-38.87,34.24ZM71.29,166.24c10.75,0,17.86-5.79,17.86-15.71s-7.28-15.88-18.03-15.88h-17.7v31.59h17.86ZM69.14,116.12c10.75,0,15.88-5.29,15.88-13.73s-5.13-13.89-15.55-13.89h-16.05v27.62h15.71ZM206.17,185.92v-48.63h-50.29v48.63h-24.15v-115.79h24.15v44.66h50.29v-44.66h24.15v115.79h-24.15Z"
                  />
                </svg>
              ) : (
                <div className={classes.expandedLogo}>{logo}</div>
              )}
            </UnstyledButton>

            <UnstyledButton
              className={classes.searchButton}
              onClick={() => setSpotlightOpen(true)}
              aria-label="Open search"
            >
              <IconSearch size={16} />
              {!isCollapsed && <span>Search</span>}
            </UnstyledButton>

            <ClinicalNotificationBell
              collapsed={isCollapsed}
              className={classes.notificationButton}
              iconClassName={classes.notificationIcon}
            />

            <nav aria-label="Main navigation">
              {menus.map((menu) => (
                <Stack key={menu.title} gap="0" className={classes.menuSection}>
                  {!isCollapsed && (
                    <Text className={classes.menuTitle}>{menu.title}</Text>
                  )}
                  {menu.links.map((link) => {
                    const isActive = activeLink?.href === link.href;
                    const commonClass = `${classes.navLink} ${isActive ? classes.navLinkActive : ""}`;

                    if (link.disabled) {
                      return (
                        <Tooltip
                          key={link.href}
                          label={link.tooltip ?? "Coming soon"}
                          withArrow
                        >
                          <button
                            type="button"
                            className={commonClass}
                            onClick={() => onNavClick(link.href, true)}
                            disabled={true}
                            title={link.tooltip ?? "Coming soon"}
                            aria-label={link.label}
                          >
                            <span className={classes.navIcon}>{link.icon}</span>
                            {!isCollapsed && <span>{link.label}</span>}
                          </button>
                        </Tooltip>
                      );
                    }

                    return (
                      <Link
                        key={link.href}
                        to={link.href}
                        className={commonClass}
                        data-active={isActive || undefined}
                        onClick={(event) => {
                          event.preventDefault();
                          onNavClick(link.href);
                        }}
                        aria-label={link.label}
                      >
                        <span className={classes.navIcon}>{link.icon}</span>
                        {!isCollapsed && <span>{link.label}</span>}
                      </Link>
                    );
                  })}
                </Stack>
              ))}
            </nav>
          </Stack>

          <Stack gap={6} className={classes.bottomSection}>
            {(demoMode || (version && !isCollapsed)) && (
              <Group
                gap={4}
                wrap="nowrap"
                justify={isCollapsed ? "center" : "space-between"}
                className={classes.versionRow}
              >
                {version && !isCollapsed && (
                  <Text
                    className={classes.versionLabel}
                    aria-label={`App version ${version}`}
                    title={`App version ${version}`}
                  >
                    v{version}
                  </Text>
                )}
                {demoMode && (
                  <DemoModeToggle
                    enabled={demoMode.enabled}
                    onToggle={demoMode.onToggle}
                    collapsed={isCollapsed}
                  />
                )}
              </Group>
            )}

            <Box
              className={classes.userActions}
              aria-label="User actions"
              onMouseEnter={() => setShowLogoutAction(true)}
              onMouseLeave={() => setShowLogoutAction(false)}
              onFocusCapture={() => setShowLogoutAction(true)}
              onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setShowLogoutAction(false);
                }
              }}
            >
              <UnstyledButton
                className={`${classes.profileButton} ${classes.userActionSurface} ${
                  showLogoutAction
                    ? classes.userActionHidden
                    : classes.userActionVisible
                }`}
                aria-label="User profile"
                aria-hidden={showLogoutAction}
                tabIndex={showLogoutAction ? -1 : 0}
              >
                <IconUserCircle size={20} />
                {!isCollapsed && (
                  <Stack
                    gap={0}
                    className={classes.profileText}
                    title={roleTitle}
                  >
                    <Text size="sm" fw={500} truncate>
                      {formatHumanName(profile?.name?.[0]) || "User"}
                    </Text>
                    {roleLabel && (
                      <Text size="xs" truncate className={classes.profileRole}>
                        {roleLabel}
                      </Text>
                    )}
                  </Stack>
                )}
              </UnstyledButton>

              <Button
                variant="subtle"
                color="gray"
                className={`${classes.logoutButton} ${classes.userActionSurface} ${
                  showLogoutAction
                    ? classes.userActionVisible
                    : classes.userActionHidden
                }`}
                onClick={() => void signOut()}
                aria-label="Logout"
                aria-hidden={!showLogoutAction}
                tabIndex={showLogoutAction ? 0 : -1}
                leftSection={
                  !isCollapsed ? <IconLogout size={16} /> : undefined
                }
              >
                {isCollapsed ? <IconLogout size={16} /> : "Logout"}
              </Button>
            </Box>
          </Stack>
        </Stack>
      </aside>

      <div className={classes.mainFrame}>
        <main className={classes.main}>{children}</main>
      </div>

      <Modal
        opened={spotlightOpen}
        onClose={closeSpotlight}
        centered
        withCloseButton={false}
        overlayProps={{ opacity: 0.65, blur: 0 }}
        transitionProps={{
          transition: "fade",
          duration: 180,
          timingFunction: "ease-in-out",
        }}
        styles={{
          content: {
            background: "transparent",
            border: "none",
            boxShadow: "none",
          },
          body: {
            background: "transparent",
            padding: 0,
          },
        }}
        classNames={{
          content: classes.spotlightContent,
          body: classes.spotlightBody,
        }}
      >
        <Stack gap="sm">
          <SpotlightKeyboardShortcuts />
          <TextInput
            autoFocus
            size="md"
            radius="xl"
            value={query}
            onChange={(event) => onQueryChange(event.currentTarget.value)}
            onKeyDown={onSearchInputKeyDown}
            placeholder="Search patient by name, ID, identifier, or phone"
            leftSection={<IconSearch size={16} />}
            aria-label="Patient search input"
            classNames={{
              input: classes.spotlightInput,
              section: classes.spotlightInputSection,
            }}
          />

          {showResultsPanel && (
            <Box className={classes.resultsPanel}>
              {status === "loading" && (
                <Group justify="center" py="md">
                  <Loader size="sm" />
                </Group>
              )}

              {status !== "loading" && (
                <ScrollArea.Autosize mah={320}>
                  {status === "error" && (
                    <Text
                      size="sm"
                      px="xs"
                      py="xs"
                      className={classes.spotlightMessage}
                    >
                      Search failed. Please try again.
                    </Text>
                  )}

                  {status === "empty" && (
                    <Text
                      size="sm"
                      px="xs"
                      py="xs"
                      className={classes.spotlightMessage}
                    >
                      No results found.
                    </Text>
                  )}

                  {status === "results" &&
                    results.map((patient, index) => {
                      const patientName = getPatientResultDisplayName(patient);
                      const isHighlighted = index === highlightedIndex;
                      return (
                        <button
                          key={patient.id ?? `${patientName}-${index}`}
                          type="button"
                          className={classes.searchResultButton}
                          data-active={isHighlighted || undefined}
                          onMouseEnter={() => setHighlightedIndex(index)}
                          onClick={() => selectPatient(patient)}
                        >
                          <Text
                            fw={500}
                            size="sm"
                            className={classes.spotlightResultPrimary}
                          >
                            {patientName}
                          </Text>
                          <Text
                            size="xs"
                            className={classes.spotlightResultSecondary}
                          >
                            {patient.birthDate ?? "No DOB"}
                          </Text>
                        </button>
                      );
                    })}
                </ScrollArea.Autosize>
              )}
            </Box>
          )}
        </Stack>
      </Modal>
    </div>
  );
};

/**
 * Returns persisted collapsed state with safe fallback.
 * @agentic connections
 */
const getInitialCollapsedState = (): boolean => {
  const persisted = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
  return persisted === "true";
};

/**
 * Computes the best matching active nav link based on path and query params.
 * @agentic connections
 */
const getActiveLink = (
  currentPathname: string,
  currentSearchParams: URLSearchParams,
  menus: readonly ConnectionsNavMenu[],
): ConnectionsNavLink | undefined => {
  let bestLink: ConnectionsNavLink | undefined;
  let bestScore = 0;

  for (const menu of menus) {
    for (const link of menu.links) {
      if (link.disabled) {
        continue;
      }
      const score = getLinkScore(
        currentPathname,
        currentSearchParams,
        link.href,
      );
      if (score > bestScore) {
        bestScore = score;
        bestLink = link;
      }
    }
  }

  return bestLink;
};

/**
 * Scores a candidate href match against current route path + query.
 * @agentic connections
 */
const getLinkScore = (
  currentPathname: string,
  currentSearchParams: URLSearchParams,
  href: string,
): number => {
  const linkUrl = new URL(href, "https://example.com");
  const hasSearchFilters = [...linkUrl.searchParams.keys()].length > 0;
  const isExactPathMatch = linkUrl.pathname === currentPathname;
  const isSubPathMatch =
    !hasSearchFilters &&
    currentPathname.startsWith(`${linkUrl.pathname}/`) &&
    linkUrl.pathname !== "/";

  if (!isExactPathMatch && !isSubPathMatch) {
    return 0;
  }

  if (hasSearchFilters) {
    for (const [key, value] of linkUrl.searchParams.entries()) {
      if (IGNORED_QUERY_PARAMS.includes(key)) {
        continue;
      }
      if (currentSearchParams.get(key) !== value) {
        return 0;
      }
    }
  }

  let score = linkUrl.pathname.length;
  for (const [key, value] of currentSearchParams.entries()) {
    if (IGNORED_QUERY_PARAMS.includes(key)) {
      continue;
    }
    if (linkUrl.searchParams.get(key) === value) {
      score += 1;
    }
  }

  return score;
};

/**
 * Returns the best patient label with optional preferred name in parentheses.
 * @agentic connections
 */
const getPatientResultDisplayName = (patient: Patient): string => {
  const primaryName = getPrimaryPatientName(patient);
  const primaryDisplay =
    formatHumanName(primaryName) || patient.id || "Unknown";
  const preferredName = getPreferredPatientName(patient, primaryDisplay);

  if (!preferredName) {
    return primaryDisplay;
  }

  return `${primaryDisplay} (${preferredName})`;
};

/**
 * Selects the preferred primary patient name for display.
 * @agentic connections
 */
const getPrimaryPatientName = (patient: Patient): HumanName | undefined =>
  patient.name?.find((name) => name.use === "official") ?? patient.name?.[0];

/**
 * Selects a distinct preferred/usual patient name for secondary display.
 * @agentic connections
 */
const getPreferredPatientName = (
  patient: Patient,
  primaryDisplay: string,
): string | undefined => {
  const preferred = patient.name?.find((name) => name.use === "usual");
  const preferredDisplay = formatHumanName(preferred).trim();

  if (!preferredDisplay || preferredDisplay === primaryDisplay) {
    return undefined;
  }

  return preferredDisplay;
};
